import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../ui/command';
import type { ConditionField } from '../../services/businessRuleService';

export function fieldTypeLabel(field: ConditionField): string {
    if (field.cvlId || field.datatype?.toLowerCase() === 'cvl') {
        const type = field.isLocalizedCvl ? 'Localized CVL' : 'CVL';
        return field.isMultiValue ? `Multivalue ${type}` : type;
    }
    if (['Integer', 'Double', 'Decimal', 'Number'].includes(field.datatype ?? '')) return 'Number';
    if (field.datatype === 'LocaleString') return 'Localized string';
    return field.datatype || 'String';
}

export function BusinessRuleFieldPicker({ fields, value, onChange, loading = false, error = false, label = 'Field' }: {
    fields: ConditionField[]; value?: string; onChange: (field: string) => void;
    loading?: boolean; error?: boolean; label?: string;
}) {
    const [open, setOpen] = useState(false);
    const selected = fields.find(field => field.key === value);
    return <div className="min-w-0"><Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><Button type="button" role="combobox" aria-label={label} aria-expanded={open} variant="outline" disabled={loading || error} className="w-full min-w-0 justify-between font-normal">
            <span className="truncate">{loading ? 'Loading fields…' : selected?.name || value || 'Select field'}</span><ChevronDown className="ml-2 h-4 w-4 shrink-0" />
        </Button></PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
            <Command><CommandInput placeholder="Search fields…" />
                <div className="grid grid-cols-[minmax(0,1fr)_9rem] gap-3 border-b px-3 py-2 text-xs font-semibold text-muted-foreground"><span>Name</span><span>Field type</span></div>
                <CommandList><CommandEmpty>No matching fields.</CommandEmpty>
                    {fields.map(field => <CommandItem key={field.key} value={field.key} keywords={[field.name ?? '', fieldTypeLabel(field)]} onSelect={() => { onChange(field.key); setOpen(false); }} className="grid grid-cols-[minmax(0,1fr)_9rem] items-start gap-3">
                        <span className="flex min-w-0 items-start gap-2"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${field.key === value ? 'opacity-100' : 'opacity-0'}`} /><span className="break-words">{field.name || field.key}</span></span>
                        <span className="text-xs text-muted-foreground">{fieldTypeLabel(field)}</span>
                    </CommandItem>)}
                </CommandList>
            </Command>
        </PopoverContent>
    </Popover>{error && <p role="alert" className="text-xs text-destructive">Could not load InRiver fields. Reopen the rule to retry.</p>}</div>;
}
